import { TRANSLATION_MAP } from 'src/app/enums';
import { IEventSettings, IField, IValidationResult } from '../interfaces';
import { SharedService } from '../shared.service';
import { UIState, UIParticipant, UIParticipantStatus, MultiRegistrationConfig } from '@azavista/participants/dist/event-app/multiregistration';
import { MULTI_REGISTRATION_ATTRIBUTE_NAMES } from './types';

export const getParentRecursively = <T extends HTMLElement = HTMLElement>(
    element: HTMLElement,
    parentSelector: string
): T | undefined => {
    const parentElement = element.parentElement;
    const grandParent = parentElement?.parentElement;
    if (!grandParent) {
        return undefined;
    }
    const result = grandParent.querySelector<T>(parentSelector);
    if (result) {
        return result;
    }
    const documentAny: any = document;
    if (grandParent !== documentAny && parentElement !== documentAny) {
        return getParentRecursively(parentElement, parentSelector);
    }
};

export const getAssignMainParticipantField = (params?: {
    isAssigned?: boolean,
    can_add_participant?: boolean,
    sharedSvc: SharedService,
}): IField => {
    const { isAssigned, can_add_participant, sharedSvc } = params || {};
    return {
        editable: isAssigned ? true : can_add_participant ? true : false,
        id: MULTI_REGISTRATION_ATTRIBUTE_NAMES.main_participant_assigned_field_id,
        name: MULTI_REGISTRATION_ATTRIBUTE_NAMES.main_participant_assigned_field_id,
        label: sharedSvc?.translate(
            TRANSLATION_MAP.MAIN_PARTICIPANT_TICKET_ASSIGNED
        ) || '',
        type: 'boolean',
        schema: {
            type: 'boolean',
        },
    } as IField;
};

export const findParticipantById = (participantId, state: UIState): UIParticipant | undefined => {
    const { main_participant, subparticipants } = state;
    if (participantId === main_participant.id) {
        return main_participant;
    }
    return subparticipants.find(({id}) => id === participantId);
};

export const getIsRequireUniqueEmail = (eventSettings: IEventSettings): boolean => {
    return eventSettings?.duplicate_email !== true;
};

export const getExistingMultiRegistrationEmails = (state: UIState, fieldId: string, excludedParticipantId?: string): string[] => {
    const { subparticipants, main_participant } = state;
    return [
        ...subparticipants,
        main_participant,
    ].reduce((result: string[], participant) => {
        if (participant.id !== excludedParticipantId) {
            result.push(participant.participant_attributes?.[fieldId]);
        }
        return result;
    }, []);
};

export const getUniqueEmailValidation = (state: UIState, fieldId: string, emailValue: string, excludedParticipantId?: string): IValidationResult => {
    const existingEmails =  getExistingMultiRegistrationEmails(state, fieldId, excludedParticipantId);
    const isValid = !existingEmails.includes(emailValue);
    const result: IValidationResult = {
        value: emailValue,
        errorsText: !isValid ? 'Email has been taken' : '',
        isValid,
        isRequiredButNotProvided: !emailValue
    };
    return result;
};

export const getFieldWithUniqueSubParticipantEmail = (state: UIState, eventSettings: IEventSettings, field: IField,) => {
    field = {
        ...field
    };
    if (getIsRequireUniqueEmail(eventSettings)) {
        if (field.name === 'email') {
            Object.assign(field.schema, {
                not: {
                    enum: getExistingMultiRegistrationEmails(state, field.id)
                }
            } as any);
        }
    }

    return field;
};

export const getStateHasError = (state: UIState) => {
    const { main_participant, subparticipants, error } = state;
    if (error?.code) {
        return true;
    }
    const allParticipants = [ main_participant, ...subparticipants];
    return allParticipants.some(participant => !!participant?.error?.code);
};

export const getFieldWithParticipantStatus = (status: UIParticipantStatus, field: IField, participant: UIParticipant): IField => {
    return {
        ...field,
        editable: participant.editable && getFieldEditableByStatus(status),
    };
};

export const getFieldEditableByStatus = (status: UIParticipantStatus): boolean => {
    return !(
        status === UIParticipantStatus.SCANNED
    );
};

export const getIsRequireTicket = (config: Pick<MultiRegistrationConfig, 'multiregistration_type'>) => {
    return config?.multiregistration_type === 'ticket';
};
